import { useApiQuery } from '../useApiQuery';
import { bookingKeys } from '@/lib/queryKeys';
import { schedulerEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import { dateStringToDate, zonedDateTimeToUtc } from '@/lib/date-values';
import type {
  Booking,
  BookingListParams,
  BookingListResponse,
  BookingStatus,
} from '@/types/booking';

export function mapAppointmentStatus(s: string): BookingStatus {
  const m: Record<string, BookingStatus> = {
    RESERVED: 'scheduled',
    SCHEDULED: 'scheduled',
    CONFIRMED: 'scheduled',
    UNSCHEDULED: 'unscheduled',
    DISPATCHED: 'in-progress',
    IN_PROGRESS: 'in-progress',
    ON_HOLD: 'in-progress',
    CANCELLATION_PENDING: 'cancelled',
    CANCELLED: 'cancelled',
    SKIPPED: 'completed',
    EXPIRED: 'completed',
    COMPLETED: 'completed',
    // pass-through known BookingStatus values unchanged
    scheduled: 'scheduled',
    'in-progress': 'in-progress',
    completed: 'completed',
    cancelled: 'cancelled',
    unscheduled: 'unscheduled',
  };
  return m[s] ?? 'unscheduled';
}

function toStatusGroup(status?: BookingStatus | '') {
  if (!status) return undefined;
  return status;
}

function toSortBy(sortBy?: BookingListParams['sortBy']) {
  if (sortBy === 'scheduledStart') return 'startAt';
  if (sortBy === 'status') return 'status';
  return 'createdAt';
}

function pickItems(raw: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(raw)) return raw as Array<Record<string, unknown>>;
  if (!raw || typeof raw !== 'object') return [];
  const obj = raw as Record<string, unknown>;
  const items = obj.items ?? obj.data ?? obj.bookings ?? obj.detailData;
  return Array.isArray(items) ? (items as Array<Record<string, unknown>>) : [];
}

/**
 * §7B — a day-bound visit (DAY_FLEXIBLE awaiting the route optimizer) has no
 * startAt; the API carries its tenant-local day as `scheduledDate` (YYYY-MM-DD).
 * Anchor that day to midnight in the row's timezone so the list's existing
 * instant-based date rendering shows the RIGHT day — a raw date-only string
 * parses as UTC midnight and shifts a day back in negative-offset (US) zones.
 * Exported for the sibling raw-booking mappers (e.g. useUpcomingJobs).
 */
export function dayBoundStart(b: Record<string, unknown>): string | null {
  if (typeof b.scheduledDate !== 'string' || !b.scheduledDate) return null;
  const tz = typeof b.timezone === 'string' && b.timezone ? b.timezone : undefined;
  return zonedDateTimeToUtc(dateStringToDate(b.scheduledDate), '00:00', tz)?.toISOString() ?? null;
}

export function toBookingRow(b: Record<string, unknown>): Booking {
  const contact = b.contact as Record<string, unknown> | undefined;
  const serviceType = b.serviceType as Record<string, unknown> | undefined;
  const jobType = b.jobType as Record<string, unknown> | undefined;
  const customerName = String(
    b.customerName ?? [contact?.firstName, contact?.lastName].filter(Boolean).join(' ') ?? '',
  );
  // A timed instant when one exists (TIMED / ALL_DAY / sequenced DAY_FLEXIBLE),
  // else the day-bound anchor — never ignore the API's real scheduledDate: a
  // day-bound visit IS scheduled (to a day), not unscheduled.
  const startInstant = b.startAt ?? b.scheduledStart ?? dayBoundStart(b);
  const hasSchedule = Boolean(startInstant);
  const rawStatus = String(b.status ?? '');

  return {
    id: String(b.bookingId ?? b.id ?? ''),
    requestNumber: String(b.requestNumber ?? ''),
    jobId: typeof b.jobId === 'string' ? b.jobId : null,
    appointmentId:
      typeof b.appointmentId === 'string' && b.appointmentId.trim() ? b.appointmentId.trim() : null,
    appointmentNumber: typeof b.appointmentNumber === 'number' ? b.appointmentNumber : null,
    customerName,
    status:
      !hasSchedule && rawStatus === 'RESERVED' ? 'unscheduled' : mapAppointmentStatus(rawStatus),
    serviceType: String(serviceType?.name ?? b.serviceType ?? ''),
    serviceName: String(jobType?.name ?? serviceType?.name ?? b.serviceName ?? '') || undefined,
    jobType: jobType?.id
      ? {
          id: String(jobType.id),
          name: String(jobType.name ?? ''),
          color: String(jobType.color ?? '#2196F3'),
          icon: typeof jobType.icon === 'string' ? jobType.icon : null,
        }
      : null,
    scheduledDate: String(startInstant ?? ''),
    scheduledStart: String(b.startAt ?? b.scheduledStart ?? '') || undefined,
    timezone: String(b.timezone ?? '') || undefined,
    siteTimezone: String(b.siteTimezone ?? '') || undefined,
    address: b.address,
    phone: String(b.phone ?? contact?.phone ?? ''),
    technicianName: String(b.technicianName ?? ''),
    technicianIds: Array.isArray(b.technicianIds) ? b.technicianIds : [],
    technicianNames: Array.isArray(b.technicianNames) ? b.technicianNames : [],
    createdAt: String(b.createdAt ?? ''),
    updatedAt: String(b.updatedAt ?? b.createdAt ?? ''),
  } as unknown as Booking;
}

export function useBookings(params: BookingListParams = {}) {
  return useApiQuery<BookingListResponse>({
    queryKey: bookingKeys.list(params),
    queryFn: async () => {
      const limit = params.limit ?? 20;
      const page = params.page ?? 1;

      const apiParams: Record<string, unknown> = {
        limit,
        offset: (page - 1) * limit,
      };
      if (params.search) apiParams.search = params.search;
      const statusGroup = toStatusGroup(params.status);
      if (statusGroup) apiParams.statusGroup = statusGroup;
      if (params.technicianId) apiParams.technicianId = params.technicianId;
      if (params.dateFrom) apiParams.dateFrom = params.dateFrom;
      if (params.dateTo) apiParams.dateTo = params.dateTo;
      if (params.excludeUnscheduled) apiParams.excludeUnscheduled = 'true';
      if (params.hasJob !== undefined) apiParams.hasJob = params.hasJob ? 'true' : 'false';
      if (params.sortBy) apiParams.sortBy = toSortBy(params.sortBy);
      if (params.sortOrder) apiParams.sortOrder = params.sortOrder;
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        schedulerEndpoints.bookings(),
        { params: apiParams },
      );
      const items = pickItems(raw);
      return {
        bookings: items.filter((b) => b.bookingId ?? b.id).map(toBookingRow),
        total: Number(raw.total ?? items.length),
        page: Number(raw.page ?? params.page ?? 1),
        limit: Number(raw.limit ?? params.limit ?? 25),
      } as unknown as BookingListResponse;
    },
    showErrorToast: false,
  });
}
